(function () {
  'use strict';

  const WAVE_DURATION = 2.35;
  const LOOP_PAUSE = 0.45;
  const DURATION = WAVE_DURATION + LOOP_PAUSE;
  const SHINE_BAND_RATIO = 0.18;
  const SWEEP_ANGLE = (20 * Math.PI) / 180;

  const root = document.getElementById('OdysseeIntro');
  if (!root) return;

  const bar = root.querySelector('[data-intro-bar]');
  const barWrap = root.querySelector('[data-intro-bar-wrap]');
  const enterBtn = root.querySelector('[data-intro-enter]');
  const shineGrad = document.getElementById('odysseeShineGrad');
  const shineLayer = root.querySelector('[data-intro-shine-layer]');
  const shineSvg = root.querySelector('.odyssee-intro__shine-svg');
  const skyCanvas = root.querySelector('[data-intro-sky]');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let shineActive = false;
  let skyActive = false;
  let skyRaf = 0;
  let shineStartTs = null;
  let contentMinX = 0;
  let contentMaxX = 1024;
  let contentMidY = 170.5;
  let shineBand = 930 / 2550 * 1024;

  function forceIntro() {
    return new URLSearchParams(window.location.search).get('intro') === '1';
  }

  function shouldSkipIntro() {
    if (forceIntro()) return false;
    if (root.getAttribute('data-once') !== 'true') return false;
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type === 'reload') return false;
      if (document.referrer && new URL(document.referrer).origin === location.origin) return true;
    } catch (e) {}
    return false;
  }

  function rememberIntro() {
    try {
      sessionStorage.setItem('nebulyss-intro-seen', '1');
    } catch (e) {}
  }

  function preventScroll(e) {
    e.preventDefault();
  }

  function lockScroll() {
    document.documentElement.classList.add('home-scroll-locked');
    document.body.classList.add('home-scroll-locked');
    window.scrollTo(0, 0);
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });
  }

  function unlockScroll() {
    document.documentElement.classList.remove('home-scroll-locked');
    document.body.classList.remove('home-scroll-locked');
    document.removeEventListener('wheel', preventScroll);
    document.removeEventListener('touchmove', preventScroll);
  }

  function hideIntro() {
    shineActive = false;
    skyActive = false;
    if (skyRaf) cancelAnimationFrame(skyRaf);
    document.documentElement.classList.remove('odyssee-intro-pending', 'odyssee-intro-press', 'odyssee-intro-leave');
    root.hidden = true;
    unlockScroll();
  }

  function revealStore() {
    root.setAttribute('aria-busy', 'false');
    document.documentElement.classList.remove('odyssee-intro-pending', 'odyssee-intro-press', 'odyssee-intro-leave');
    root.style.pointerEvents = 'none';
    hideIntro();
    window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  function waveEnvelope(t) {
    if (t >= WAVE_DURATION) return 0;
    return Math.sin((t / WAVE_DURATION) * Math.PI);
  }

  function measureShineContent() {
    if (!shineSvg) return;
    const box = shineSvg.viewBox.baseVal;
    contentMinX = box.x;
    contentMaxX = box.x + box.width;
    contentMidY = box.y + box.height / 2;
    shineBand = (contentMaxX - contentMinX) * SHINE_BAND_RATIO;
  }

  function setGradientAt(centerX, centerY) {
    if (!shineGrad) return;
    const cos = Math.cos(SWEEP_ANGLE);
    const sin = Math.sin(SWEEP_ANGLE);
    shineGrad.setAttribute('x1', String(centerX - cos * shineBand));
    shineGrad.setAttribute('y1', String(centerY - sin * shineBand));
    shineGrad.setAttribute('x2', String(centerX + cos * shineBand));
    shineGrad.setAttribute('y2', String(centerY + sin * shineBand));
  }

  function applySweep(elapsed) {
    const t = elapsed % DURATION;
    const envelope = reduceMotion ? 1 : Math.min(1, waveEnvelope(t) * 1.2);

    if (shineLayer) {
      shineLayer.setAttribute('opacity', String(envelope));
    }

    if (t >= WAVE_DURATION) return;

    const progress = t / WAVE_DURATION;
    const span = contentMaxX - contentMinX;
    const centerX = contentMinX + progress * (span + shineBand * 2) - shineBand;
    setGradientAt(centerX, contentMidY);
  }

  function tickShine(ts) {
    if (!shineActive) return;
    if (!shineStartTs) shineStartTs = ts;
    applySweep((ts - shineStartTs) / 1000);
    requestAnimationFrame(tickShine);
  }

  function startStarfield() {
    if (!skyCanvas || skyActive) return;

    const ctx = skyCanvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const stars = [];
    const shooting = [];
    let width = 0;
    let height = 0;
    let dpr = 1;
    let lastSpawn = 0;
    let nextSpawn = 380;

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = root.clientWidth || window.innerWidth;
      height = root.clientHeight || window.innerHeight;
      skyCanvas.width = Math.round(width * dpr);
      skyCanvas.height = Math.round(height * dpr);
      skyCanvas.style.width = `${width}px`;
      skyCanvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function seedStars() {
      stars.length = 0;
      const count = Math.round(Math.min(260, Math.max(130, (width * height) / 8000)));
      for (let i = 0; i < count; i += 1) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 1.15 + 0.25,
          base: Math.random() * 0.45 + 0.25,
          amp: Math.random() * 0.55 + 0.2,
          speed: Math.random() * 1.6 + 0.6,
          phase: Math.random() * Math.PI * 2,
          gold: Math.random() > 0.35,
        });
      }
    }

    function spawnShooting(now) {
      const fromRight = Math.random() > 0.35;
      shooting.push({
        x: fromRight ? width * (0.2 + Math.random() * 0.75) : width * Math.random() * 0.4,
        y: height * Math.random() * 0.45,
        vx: fromRight ? -(380 + Math.random() * 220) : 360 + Math.random() * 200,
        vy: 160 + Math.random() * 140,
        life: 0,
        max: 0.55 + Math.random() * 0.35,
        len: 70 + Math.random() * 90,
      });
      lastSpawn = now;
      nextSpawn = 900 + Math.random() * 1100;
    }

    function drawStars(now) {
      const t = now / 1000;
      for (let i = 0; i < stars.length; i += 1) {
        const star = stars[i];
        const twinkle = reduceMotion ? star.base : star.base + Math.sin(t * star.speed + star.phase) * star.amp;
        const alpha = Math.max(0.08, Math.min(1, twinkle));
        ctx.beginPath();
        ctx.fillStyle = star.gold
          ? `rgba(248, 208, 64, ${alpha})`
          : `rgba(255, 246, 220, ${alpha})`;
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function drawShooting(dt, now) {
      if (!reduceMotion && now - lastSpawn > nextSpawn) {
        spawnShooting(now);
      }

      for (let i = shooting.length - 1; i >= 0; i -= 1) {
        const s = shooting[i];
        s.life += dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;

        const fade = 1 - s.life / s.max;
        if (fade <= 0) {
          shooting.splice(i, 1);
          continue;
        }

        const angle = Math.atan2(s.vy, s.vx);
        const tailX = s.x - Math.cos(angle) * s.len;
        const tailY = s.y - Math.sin(angle) * s.len;
        const grad = ctx.createLinearGradient(tailX, tailY, s.x, s.y);
        grad.addColorStop(0, 'rgba(255, 236, 159, 0)');
        grad.addColorStop(0.55, `rgba(255, 226, 140, ${0.25 * fade})`);
        grad.addColorStop(1, `rgba(255, 246, 210, ${0.95 * fade})`);

        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.4;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 248, 220, ${0.9 * fade})`;
        ctx.arc(s.x, s.y, 1.35, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    let lastTs = 0;
    function tick(ts) {
      if (!skyActive) return;
      if (!lastTs) lastTs = ts;
      const dt = Math.min(0.032, (ts - lastTs) / 1000);
      lastTs = ts;

      ctx.clearRect(0, 0, width, height);
      drawStars(ts);
      drawShooting(dt, ts);
      skyRaf = requestAnimationFrame(tick);
    }

    resize();
    seedStars();
    skyActive = true;
    lastSpawn = 0;
    nextSpawn = 320;
    window.addEventListener('resize', () => {
      if (!skyActive) return;
      resize();
      seedStars();
    });
    skyRaf = requestAnimationFrame(tick);
  }

  function startShine() {
    if (shineActive || !shineGrad || !shineLayer) return;
    measureShineContent();
    shineActive = true;
    shineStartTs = null;
    requestAnimationFrame(tickShine);
  }

  function whenImageReady(img) {
    if (!img) return Promise.resolve();
    const src = img.currentSrc || img.getAttribute('src') || '';
    if (!src) return Promise.resolve();
    if (img.complete && img.naturalWidth) {
      return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    }
    return new Promise((resolve) => {
      const done = () => {
        if (img.decode && img.naturalWidth) {
          img.decode().then(resolve).catch(resolve);
        } else {
          resolve();
        }
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function warmPageImages() {
    return Promise.allSettled(
      [...document.querySelectorAll('img')].map((img) => {
        if (root.contains(img)) return Promise.resolve();
        try {
          img.loading = 'eager';
          img.setAttribute('loading', 'eager');
        } catch (e) {}
        return whenImageReady(img);
      })
    );
  }

  function prefetchStorePages() {
    const seen = new Set();
    const nodes = document.querySelectorAll(
      'header a[href], .menu-drawer a[href], .header__inline-menu a[href], .odyssee-sig__cta[href], #MainContent a[href]'
    );

    nodes.forEach((anchor) => {
      const raw = anchor.getAttribute('href');
      if (!raw || raw.startsWith('#') || raw.startsWith('mailto:') || raw.startsWith('tel:') || raw.startsWith('javascript:')) {
        return;
      }
      try {
        const url = new URL(raw, location.href);
        if (url.origin !== location.origin) return;
        if (url.pathname === location.pathname && url.search === location.search) return;
        const key = url.pathname + url.search;
        if (seen.has(key) || seen.size >= 8) return;
        seen.add(key);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url.href;
        link.as = 'document';
        document.head.appendChild(link);
      } catch (e) {}
    });

    window.setTimeout(() => {
      const primary = document.querySelector('.odyssee-sig__cta[href], a[href*="/collections/"]');
      if (!primary || !HTMLScriptElement.supports || !HTMLScriptElement.supports('speculationrules')) return;
      try {
        const url = new URL(primary.getAttribute('href'), location.href).href;
        const spec = document.createElement('script');
        spec.type = 'speculationrules';
        spec.textContent = JSON.stringify({
          prerender: [{ source: 'list', urls: [url], eagerness: 'immediate' }],
        });
        document.head.appendChild(spec);
      } catch (e) {}
    }, 1100);
  }

  function warmHeroSliderImages() {
    if (!document.querySelector('odyssee-hero-slider')) return Promise.resolve();

    return customElements.whenDefined('odyssee-hero-slider').then(() => {
      const slider = document.querySelector('odyssee-hero-slider');
      if (slider && typeof slider.preloadImages === 'function') {
        return slider.preloadImages();
      }
    });
  }

  if (
    window.Shopify?.designMode ||
    shouldSkipIntro() ||
    root.hidden ||
    !document.documentElement.classList.contains('odyssee-intro-pending')
  ) {
    hideIntro();
    return;
  }

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  lockScroll();
  window.scrollTo(0, 0);
  startShine();

  let revealed = false;

  function enterSite() {
    if (revealed) return;
    revealed = true;
    rememberIntro();
    revealStore();
  }

  const barMs = reduceMotion ? 400 : 2800;
  const introStart = typeof window.__odysseeIntroStart === 'number' ? window.__odysseeIntroStart : performance.now();
  const wait = Math.max(0, barMs - (performance.now() - introStart));

  if (wait <= 16) enterSite();
  else window.setTimeout(enterSite, wait);
})();
